import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';

export interface NPSRatingProps {
  value: number;
  onChange: (value: number) => void;
  max?: number;
  required?: boolean;
  useEmoji?: boolean;
  error?: boolean;
}

export default function NPSRating({
  value,
  onChange,
  max = 5,
  required = false,
  useEmoji = true,
  error = false,
}: NPSRatingProps) {
  const getEmoji = (score: number) => {
    const emojis: Record<number, string> = {
      1: '😡',
      2: '😕',
      3: '😐',
      4: '🙂',
      5: '😍',
    };
    return emojis[score] || '😶';
  };

  // Determine if we need to use a wrapped layout for mobile
  const needsWrappedLayout = max > 7;

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: needsWrappedLayout ? { xs: 'wrap', sm: 'wrap', md: 'nowrap' } : 'nowrap',
          justifyContent: 'space-between',
          gap: { xs: 0.5, sm: 1 },
          ...(error && {
            border: '1px solid rgba(255, 80, 80, 0.3)',
            borderRadius: 1,
            p: 1,
            backgroundColor: 'rgba(255, 80, 80, 0.05)',
          }),
        }}
      >
        {Array.from({ length: max }, (_, i) => i + 1).map((score) => {
          const isSelected = value === score;
          const isHeart = !useEmoji;
          const isHeartSelected = value >= score && isHeart;

          return (
            <Button
              key={score}
              onClick={() => onChange(score)}
              disableRipple
              sx={{
                // Adjust flex basis to create rows of 5 items on mobile
                flex: needsWrappedLayout ? { xs: '0 0 18%', sm: '0 0 19%', md: 1 } : 1,
                minWidth: { xs: '30px', sm: '32px' },
                height: { xs: '40px', sm: '45px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 1,
                px: { xs: 0.5, sm: 1 },
                border: 'none',
                borderRadius: 1,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                mb: needsWrappedLayout ? { xs: 1, sm: 1, md: 0 } : 0,
                transition: 'all 0.2s',
              }}
            >
              {useEmoji ? (
                // Emoji rating style
                <Box
                  sx={{
                    fontSize: { xs: '20px', sm: '24px' },
                    mb: 0.5,
                    opacity: isSelected ? 1 : 0.5, // Grayed out when not selected
                    filter: isSelected ? 'none' : 'grayscale(0.8)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      opacity: 0.8,
                      filter: 'grayscale(0.3)',
                    },
                  }}
                >
                  {getEmoji(score)}
                </Box>
              ) : (
                // Heart rating style - white when selected
                <Box
                  sx={{
                    width: { xs: '20px', sm: '24px' },
                    height: { xs: '20px', sm: '24px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isHeartSelected ? 'white' : 'rgba(255, 255, 255, 0.5)',
                    mb: 0.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: isHeartSelected ? 'white' : 'rgba(255, 255, 255, 0.8)',
                    },
                  }}
                >
                  {isHeartSelected ? (
                    // Filled heart - white
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  ) : (
                    // Outlined heart
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  )}
                </Box>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: isSelected || isHeartSelected ? 'white' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                }}
              >
                {score}
              </Typography>
            </Button>
          );
        })}
      </Box>

      {max === 10 && !useEmoji && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 1 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 100, 100, 0.8)' }}>
            Not likely
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(100, 255, 100, 0.8)' }}>
            Very likely
          </Typography>
        </Box>
      )}
    </Box>
  );
}
